import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { NewHiresService } from '@app/+authenticated/+employees/new-hires/services/new-hires.service';
import type { NewHireSectionedFormComponent } from '@app/+new-hire/sectioned-form/new-hire-sectioned-form/new-hire-sectioned-form.component';
import { ButtonComponent } from '@sb/ui/button';
import { filter, firstValueFrom, map, take } from 'rxjs';

import { autofillHiringForm } from './dev-tools-hiring-fill.helper';
import { DevToolsSectionComponent } from './dev-tools-section.component';

// Angular's debug global (available in dev mode only) — lets the dev tool grab
// the live form component instance without coupling to it at runtime.
declare global {
  interface Window {
    ng?: { getComponent<T>(element: Element): T | undefined };
  }
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-dev-tools-hiring',
  template: `
    <app-dev-tools-section title="Hiring" storageKey="hiring">
      <div class="flex flex-wrap gap-2">
        <button sb-button size="sm" [disabled]="!isOnNewHire()" (click)="fillForm()">Fill new-hire form</button>
      </div>
      @if (!isOnNewHire()) {
        <p class="mt-sm text-xs text-subtle">Open a new-hire form to enable this.</p>
      }
    </app-dev-tools-section>
  `,
  imports: [ButtonComponent, DevToolsSectionComponent],
})
export class DevToolsHiringComponent {
  #router = inject(Router);
  #newHires = inject(NewHiresService);
  #destroyRef = inject(DestroyRef);

  #currentUrl = toSignal(
    this.#router.events.pipe(
      filter((routerEvent): routerEvent is NavigationEnd => routerEvent instanceof NavigationEnd),
      map((navigationEnd) => navigationEnd.urlAfterRedirects),
    ),
    { initialValue: this.#router.url },
  );

  isOnNewHire = computed(() => this.#currentUrl()?.startsWith('/new-hire') ?? false);

  fillForm(): void {
    const host = document.querySelector('new-hire-sectioned-form');
    const formComponent = host ? window.ng?.getComponent<NewHireSectionedFormComponent>(host) : undefined;
    if (!formComponent) {
      console.warn('DevTools: could not resolve the new-hire form component (dev mode only).');
      return;
    }

    // Re-fetch the same schema the form loaded (keeps everything inside dev-tools —
    // no global interceptor needed), then fill scalars, children rows and files.
    this.#newHires
      .getFormQuestions(formComponent.token, formComponent.locale)
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe((questions) => {
        if (formComponent.state() === 'welcome') formComponent.start();
        autofillHiringForm({
          form: formComponent.form(),
          questions,
          uploadFile: (file) =>
            firstValueFrom(
              this.#newHires.uploadIdDocuments(formComponent.token, file).pipe(map((result) => result.resource_id)),
            ),
          addChildRow: (field) => formComponent.onAddChild(field),
        })
          .then(() => this.#unlockSteps(formComponent))
          .catch((autofillError: unknown) => console.error('DevTools: new-hire autofill failed', autofillError));
      });
  }

  // Walk Next through each (now-valid) section so the component marks them
  // completed — which is what makes the step menu clickable — then return to
  // the first step. Stops early if a section fails validation.
  #unlockSteps(formComponent: NewHireSectionedFormComponent): void {
    const sectionCount = formComponent.sections().length;
    for (let step = 0; step < sectionCount; step += 1) {
      const activeStep = formComponent.current();
      formComponent.onNext();
      if (formComponent.state() === 'review') break;
      if (formComponent.current() === activeStep) break;
    }
    formComponent.state.set('form');
    formComponent.current.set(0);
  }
}
